/**
 * Typed files API facade (presign, metadata, wafer image history, entity file deletes).
 */

import * as dt from '../../../shared/src/data.types';
import { filesApi } from '../api/generated/files.api.js';
import { containersApi } from '../api/generated/containers.api.js';
import { wafersApi } from '../api/generated/wafers.api.js';
import {
  type DeleteContainerFilesResponseData,
  type DeleteWaferFilesResponseData,
  type PresignedObjectUrlResponseData,
} from '@cnapse/shared/schemas.js';
import type { WaferImageHistoryItem } from '../types/files.js';

function normalizeWaferHistoryImage(raw: unknown): WaferImageHistoryItem | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const img = raw as Record<string, unknown>;
  const objectKey = typeof img.objectKey === 'string' ? img.objectKey.trim() : '';
  if (!objectKey) {
    return null;
  }

  const rawFileId = img.fileId ?? img.id;
  const fileId = Number(rawFileId);
  if (!Number.isFinite(fileId) || fileId <= 0) {
    return null;
  }

  const toDate = (value: unknown): Date | undefined => {
    if (value == null) return undefined;
    const d = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(d.getTime()) ? undefined : d;
  };

  return {
    fileId,
    objectKey,
    originalName:
      typeof img.originalName === 'string' && img.originalName.length > 0
        ? img.originalName
        : objectKey.split('/').pop() || 'fingerprint.jpg',
    mimeType:
      typeof img.mimeType === 'string' && img.mimeType.length > 0
        ? img.mimeType
        : 'image/jpeg',
    size:
      typeof img.size === 'number'
        ? img.size
        : Number.parseInt(String(img.size ?? '0'), 10) || 0,
    uploadedAt: toDate(img.uploadedAt),
    entityType: typeof img.entityType === 'string' ? img.entityType : undefined,
    entityId:
      img.entityId != null && img.entityId !== ''
        ? Number(img.entityId)
        : null,
    fileCategory: typeof img.fileCategory === 'string' ? img.fileCategory : undefined,
    uploadedByUserId:
      img.uploadedByUserId != null && img.uploadedByUserId !== ''
        ? Number(img.uploadedByUserId)
        : null,
    createdAt: toDate(img.createdAt),
  };
}

class FilesService {
  async getPresignedObjectUrls(
    objectKeys: string[],
    expirySeconds?: number,
    _timeoutMs?: number,
  ): Promise<PresignedObjectUrlResponseData> {
    return filesApi.getPresignedObjectUrls({
      objectKeys,
      expirySeconds: expirySeconds ?? 24 * 60 * 60,
    }) as Promise<PresignedObjectUrlResponseData>;
  }

  async getFileMetadata(
    objectKeys: string[],
  ): Promise<Record<string, { originalName: string; size: number }>> {
    if (objectKeys.length === 0) {
      return {};
    }
    return filesApi.getFileMetadata({ objectKeys }) as Promise<
      Record<string, { originalName: string; size: number }>
    >;
  }

  async getWaferImageHistory(waferId: number): Promise<{
    waferId: number;
    waferFingerprint?: string;
    images: WaferImageHistoryItem[];
  }> {
    const data = (await filesApi.getWaferImageHistory(waferId)) as {
      waferId?: number;
      waferFingerprint?: string;
      images?: unknown[];
    } | undefined;

    if (!data) {
      throw new Error('Failed to get wafer image history');
    }

    const rawImages = Array.isArray(data.images) ? data.images : [];
    const images = rawImages
      .map((img) => normalizeWaferHistoryImage(img))
      .filter((img): img is WaferImageHistoryItem => img != null);

    return {
      waferId: Number(data.waferId ?? waferId),
      waferFingerprint:
        typeof data.waferFingerprint === 'string' ? data.waferFingerprint : undefined,
      images,
    };
  }

  async deleteEntityFiles(
    entityType: 'container' | 'wafer',
    entityId: string | number,
    objectKeys: string[],
    fileType: 'image' | 'file' | 'fingerprint',
  ): Promise<DeleteContainerFilesResponseData | DeleteWaferFilesResponseData> {
    if (objectKeys.length === 0) {
      return { deletedCount: 0, objectKeys: [] };
    }

    const fileCategory =
      fileType === 'image'
        ? dt.FILE_CATEGORY.Image
        : fileType === 'file'
          ? dt.FILE_CATEGORY.Document
          : dt.FILE_CATEGORY.FingerprintImage;

    const body = { objectKeys, fileType: fileCategory };

    if (entityType === 'container') {
      return containersApi.deleteFiles(entityId, body) as Promise<DeleteContainerFilesResponseData>;
    }

    return wafersApi.deleteFiles(entityId, body) as Promise<DeleteWaferFilesResponseData>;
  }

  async deleteEntityFile(
    entityType: 'container' | 'wafer',
    entityId: string | number,
    objectKey: string,
    fileType: 'image' | 'file' | 'fingerprint',
  ): Promise<DeleteContainerFilesResponseData | DeleteWaferFilesResponseData> {
    return this.deleteEntityFiles(entityType, entityId, [objectKey], fileType);
  }
}

export const filesService = new FilesService();
